import { type PropsWithChildren, useMemo } from 'react';

import { ChartVariantContext } from '../../core/context/ChartVariantContext.js';
import { ColorRule } from '../../core/slots/ColorRule.js';
import type { ChartDatapoint } from '../../core/types/chart-data.js';
import { XYChart } from '../../xy-chart/XYChartRoot.js';
import { FlameTooltip } from '../shared/components/tooltip/FlameTooltip.js';
import {
  CHILDREN_COUNT_FIELD,
  DEPTH_FIELD,
  DISPLAY_NAME_FLAME_GRAPH,
  FLAME_ORIGINAL_IDX,
  XY_DEFAULTS,
} from '../shared/constants.js';
import { iterateSlots } from './utils/iterate-slots.js';
import { FlameGraphTooltipSlot as Tooltip } from '../shared/slots/Tooltip.js';
import type { FlameGraphProps } from '../shared/types/flame-chart.js';
import { aggregateSpansToFlameGraph } from '../shared/utils/aggregate-spans-to-flame-graph.js';

const CROSSHAIR_VARIANT = {
  crosshair: {
    horizontalHidden: true,
    verticalHidden: true,
    cursor: 'default' as const,
  },
};

/**
 * @internal
 */
export const FlameGraphRoot = /* @__PURE__ */ <
  TData extends ChartDatapoint = ChartDatapoint,
>({
  data,
  idAccessor,
  parentIdAccessor,
  valueAccessor,
  selfValueAccessor,
  labelAccessor,
  sortAccessor,
  colorPalette,
  orientation = 'bottom-up',
  truncationMode,
  valueFormatter,
  currentZoom,
  seriesActions,
  height,
  children,
}: PropsWithChildren<FlameGraphProps<TData>>) => {
  const aggregatedData = useMemo(
    () =>
      aggregateSpansToFlameGraph(
        data.map((datum, index) => ({
          ...datum,
          [FLAME_ORIGINAL_IDX]: index,
        })),
        {
          idAccessor,
          parentIdAccessor,
          labelAccessor,
          valueAccessor,
          selfValueAccessor,
          sortAccessor,
        },
      ),
    [
      data,
      idAccessor,
      parentIdAccessor,
      labelAccessor,
      valueAccessor,
      selfValueAccessor,
      sortAccessor,
    ],
  );

  const { customTooltipTemplate, colorRules } = iterateSlots<TData>(children);

  return (
    <ChartVariantContext.Provider value={CROSSHAIR_VARIANT}>
      <XYChart
        data={aggregatedData}
        truncationMode={truncationMode}
        height={height}
      >
        <XYChart.RectSeries
          x0Accessor={XY_DEFAULTS.x0Accessor}
          x1Accessor={XY_DEFAULTS.x1Accessor}
          y0Accessor={DEPTH_FIELD}
          valueAccessor={valueAccessor}
          colorPalette={colorPalette}
          _labelAccessor={labelAccessor}
          xAxisId={XY_DEFAULTS.xAxisId}
          yAxisId={XY_DEFAULTS.yAxisId}
        />
        <XYChart.XAxis
          id={XY_DEFAULTS.xAxisId}
          type="numerical"
          position="top"
          hidden
          currentZoom={currentZoom}
          infiniteZoom
        />
        <XYChart.YAxis
          id={XY_DEFAULTS.yAxisId}
          type="categorical"
          position="left"
          hidden
          reversed={orientation === 'bottom-up'}
        />
        <XYChart.Legend hidden />
        <XYChart.Toolbar hidden />
        {colorRules}
        <XYChart.Tooltip>
          {(payload) => {
            const datapoint = payload.rectPayload?.datapoint as
              | TData
              | undefined;

            if (!datapoint) {
              return;
            }

            if (customTooltipTemplate) {
              const template = customTooltipTemplate(datapoint);

              if (template === null) {
                return null;
              }
              if (template) {
                return template;
              }
            }

            const childrenCount = Number(
              datapoint?.[CHILDREN_COUNT_FIELD] ?? 0,
            );
            const totalValue = Number(datapoint?.[valueAccessor] ?? 0);
            const selfValue =
              selfValueAccessor !== undefined
                ? Number(datapoint?.[selfValueAccessor] ?? 0)
                : 0;
            const subheader =
              childrenCount > 0
                ? `${childrenCount} children with ${
                    totalValue - selfValue
                  } value`
                : '';

            return (
              <FlameTooltip
                payload={payload}
                data={data}
                labelAccessor={labelAccessor}
                valueField={valueAccessor}
                subheader={subheader}
                selfValueAccessor={selfValueAccessor}
                selfDescriptionLabel="Self value"
                valueFormatter={valueFormatter}
                seriesActions={seriesActions}
              />
            );
          }}
        </XYChart.Tooltip>
      </XYChart>
    </ChartVariantContext.Provider>
  );
};

/** @internal */
export const FlameGraph = Object.assign(FlameGraphRoot, {
  Tooltip,
  ColorRule,
});

(FlameGraph as typeof FlameGraph & { displayName: string }).displayName =
  DISPLAY_NAME_FLAME_GRAPH;
